import { combineReducers, configureStore } from "@reduxjs/toolkit";
import {
  createTransform,
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from "redux-persist";
import storage from "redux-persist/lib/storage";

import { listenerMiddleware } from "@/store/middleware/listenerMiddleware";
import { loggerMiddleware } from "@/store/middleware/logger";
import companyReducer from "@/store/slices/companySlice";
import eventsReducer from "@/store/slices/eventsSlice";
import userReducer from "@/store/slices/userSlice";
import { getEventBasePath } from "@/lib/auth/session";

// ─── Per-slice persist configs ────────────────────────────────────
const userPersistConfig = {
  key: "user",
  storage,
  whitelist: ["theme"],
};

const companyPersistConfig = {
  key: `company:${getEventBasePath()}`,
  storage,
  whitelist: ["activeCompany"],
};

// Avatars are base64 and repeat across rows, so they exceed localStorage quota.
const stripAvatars = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stripAvatars);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([key]) => key !== "avatar")
        .map(([key, nested]) => [key, stripAvatars(nested)]),
    );
  }
  return value;
};

// const avatarStripTransform = createTransform(
//   (state) => stripAvatars(state),
//   (state) => state,
//   { whitelist: ["platformEventMembers", "eventContents"] },
// );

// const eventsPersistConfig = {
//   key: `events:${getEventBasePath()}`,
//   storage,
//   whitelist: [
//     "filters",
//     "currentEvent",
//     "platformEventMembers",
//     "eventContents",
//   ],
//   transforms: [avatarStripTransform],
// };

const eventsPersistConfig = {
  key: `events:${getEventBasePath()}`,
  storage,
  whitelist: [
    "filters",
    // Add "currentEvent" only if it is a small ID/string,
    // not the complete event object.
  ],
};

// ─── Root reducer ─────────────────────────────────────────────────
const rootReducer = combineReducers({
  user: persistReducer(userPersistConfig, userReducer),
  company: persistReducer(companyPersistConfig, companyReducer),
  events: persistReducer(eventsPersistConfig, eventsReducer),
  //
  // ✅ To add a new slice:
  //   1. Create src/store/slices/mySlice.ts
  //   2. Import its reducer here
  //   3. Add it to combineReducers below (with a persistConfig if needed)
  //   Example:
  //   notifications: notificationsReducer,
});

// ─── RootState derived from the reducer, NOT from store.getState.
// This is the key fix: anchoring RootState to rootReducer means
// TypeScript resolves it without needing the store to exist first,
// breaking the circular reference entirely.
export type RootState = ReturnType<typeof rootReducer>;

// ─── Store ────────────────────────────────────────────────────────
// No explicit type annotation on `store` — let configureStore infer it.
// The only source of the original "implicitly any" error was the circular
// RootState → store.getState → store loop, which is now gone.
export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    import.meta.env.DEV
      ? getDefaultMiddleware({
          serializableCheck: {
            ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
          },
        })
          .prepend(listenerMiddleware.middleware)
          .concat(loggerMiddleware)
      : getDefaultMiddleware({
          serializableCheck: {
            ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
          },
        }).prepend(listenerMiddleware.middleware),
  devTools: import.meta.env.DEV,
});

export const persistor = persistStore(store);

// ─── AppDispatch derived from the fully-inferred store ────────────
export type AppDispatch = typeof store.dispatch;
